import { useId } from 'react';

// TrendChart — the bigger sibling of ui/sparkline.tsx: a labelled chart for a
// detail view, where the sparkline is a glance-sized decoration.
//
// Dependency-free SVG on purpose (the repo has no charting library and adding
// one for three charts isn't worth the bundle). What it adds over Sparkline:
// a y baseline + max gridline with value labels, x labels, per-point hover
// titles, and a bars variant for period totals.
//
// Design-system notes: every colour is a token or currentColor so light/dark
// both work; the fill is a token wash rather than a solid; no hardcoded hex.

export interface TrendPoint {
  // Category label for the x axis ("Aug", "2026"). Kept as a caller-formatted
  // string so this component never guesses a locale or a date format.
  label: string;
  // null = genuinely no reading (e.g. a pulse run below the anonymity floor).
  // Rendered as a gap, never as zero — a gap and a zero mean different things.
  value: number | null;
  // Optional second series, drawn as a lighter overlay (bars variant only):
  // used for billed-vs-collected.
  secondary?: number | null;
}

export interface TrendChartProps {
  points: TrendPoint[];
  variant?: 'line' | 'bars';
  height?: number;
  // Formats a value for the axis + tooltips. Defaults to a plain integer.
  format?: (v: number) => string;
  // Accessible description of what the chart shows.
  ariaLabel: string;
  // Token name for the primary series colour.
  color?: string;
  // Legend labels. When secondary values exist, both are shown.
  primaryLabel?: string;
  secondaryLabel?: string;
  // Force the y axis to start at 0. Off for scores like eNPS where the
  // interesting variation is far from zero; on for counts and money, where a
  // clipped baseline would exaggerate small changes.
  zeroBased?: boolean;
}

const PAD_L = 44;
const PAD_R = 8;
const PAD_T = 10;
const PAD_B = 22;

export function TrendChart({
  points,
  variant = 'line',
  height = 180,
  format = (v) => String(Math.round(v)),
  ariaLabel,
  color = 'var(--lime)',
  primaryLabel,
  secondaryLabel,
  zeroBased = true,
}: TrendChartProps) {
  const gradientID = useId();
  const width = 640; // viewBox width; the SVG scales to its container
  const innerW = width - PAD_L - PAD_R;
  const innerH = height - PAD_T - PAD_B;

  // Plain consts, not useMemo: a series is at most a few dozen points, and
  // hooks here would have to sit above the empty-state early return (the
  // rules-of-hooks ordering constraint) for no measurable gain.
  const present = points.filter((p): p is TrendPoint & { value: number } => p.value != null);
  const hasSecondary = points.some((p) => p.secondary != null);

  if (present.length === 0) {
    return null; // callers render their own empty state
  }

  const values = [
    ...present.map((p) => p.value),
    ...points.map((p) => p.secondary).filter((v): v is number => v != null),
  ];
  const rawMax = Math.max(...values);
  const rawMin = Math.min(...values);
  // A flat series would divide by zero; give it a symmetric band so the line
  // sits in the middle instead of on an edge.
  const max = zeroBased ? Math.max(rawMax, 0) : rawMax;
  const min = zeroBased ? Math.min(rawMin, 0) : rawMin;
  const span = max - min || Math.abs(max) || 1;
  const y = (v: number) => PAD_T + innerH - ((v - min) / span) * innerH;

  // Band-centred x so bars and line points share the same anchors.
  const band = innerW / points.length;
  const cx = (i: number) => PAD_L + band * i + band / 2;

  // Label every point when there's room, otherwise every other / every third,
  // so a 12-month axis stays readable at narrow widths.
  const labelStep = points.length <= 8 ? 1 : points.length <= 16 ? 2 : 3;

  // Split into contiguous runs of present values. Gaps must break BOTH the
  // stroke and the fill: closing one path across a gap draws a polygon that
  // sweeps under the missing region (a giant wrong triangle), which is exactly
  // what a single accumulated path with one Z produced.
  const segments: { i: number; value: number }[][] = [];
  points.forEach((p, i) => {
    if (p.value == null) {
      if (segments[segments.length - 1]?.length) segments.push([]);
      return;
    }
    if (segments.length === 0) segments.push([]);
    segments[segments.length - 1]!.push({ i, value: p.value });
  });
  const drawn = segments.filter((s) => s.length > 0);
  const strokeFor = (seg: { i: number; value: number }[]) =>
    seg.map((s, k) => `${k === 0 ? 'M' : 'L'}${cx(s.i).toFixed(1)},${y(s.value).toFixed(1)}`).join(' ');
  const fillFor = (seg: { i: number; value: number }[]) =>
    `${strokeFor(seg)} L${cx(seg[seg.length - 1]!.i).toFixed(1)},${y(min).toFixed(1)} ` +
    `L${cx(seg[0]!.i).toFixed(1)},${y(min).toFixed(1)} Z`;

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full"
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradientID} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Gridlines: max and baseline only — enough to read magnitude without
            turning the panel into graph paper. */}
        {[max, min].map((v, idx) => (
          <g key={idx}>
            <line
              x1={PAD_L}
              x2={width - PAD_R}
              y1={y(v)}
              y2={y(v)}
              stroke="currentColor"
              strokeOpacity={0.12}
              strokeWidth={1}
            />
            <text
              x={PAD_L - 6}
              y={y(v) + 3}
              textAnchor="end"
              className="fill-current text-[10px] tabular-nums opacity-50"
            >
              {format(v)}
            </text>
          </g>
        ))}

        {variant === 'bars'
          ? points.map((p, i) => {
              const w = Math.max(2, band * (hasSecondary ? 0.34 : 0.55));
              const zeroY = y(Math.max(min, 0));
              return (
                <g key={i}>
                  {p.secondary != null && (
                    <rect
                      x={cx(i) - (hasSecondary ? w + 1 : w / 2)}
                      y={Math.min(y(p.secondary), zeroY)}
                      width={w}
                      height={Math.max(1, Math.abs(zeroY - y(p.secondary)))}
                      rx={2}
                      fill="currentColor"
                      fillOpacity={0.18}
                    >
                      <title>{`${p.label}: ${format(p.secondary)}${secondaryLabel ? ` (${secondaryLabel})` : ''}`}</title>
                    </rect>
                  )}
                  {p.value != null && (
                    <rect
                      x={cx(i) - (hasSecondary ? 0 : w / 2) + (hasSecondary ? 1 : 0)}
                      y={Math.min(y(p.value), zeroY)}
                      width={w}
                      height={Math.max(1, Math.abs(zeroY - y(p.value)))}
                      rx={2}
                      fill={color}
                    >
                      <title>{`${p.label}: ${format(p.value)}${primaryLabel ? ` (${primaryLabel})` : ''}`}</title>
                    </rect>
                  )}
                </g>
              );
            })
          : (
            <>
              {/* One area + one stroke PER contiguous segment. */}
              {drawn.map((seg, k) =>
                seg.length > 1 ? (
                  <path key={`a${k}`} d={fillFor(seg)} fill={`url(#${gradientID})`} stroke="none" />
                ) : null,
              )}
              {drawn.map((seg, k) => (
                <path
                  key={`s${k}`}
                  d={strokeFor(seg)}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {points.map((p, i) =>
                p.value == null ? null : (
                  <circle key={i} cx={cx(i)} cy={y(p.value)} r={3} fill={color}>
                    <title>{`${p.label}: ${format(p.value)}`}</title>
                  </circle>
                ),
              )}
            </>
          )}

        {points.map((p, i) => {
          // Force-labelling the last point on top of a stepped label overprints
          // two dates on the same spot, so the last label only appears when the
          // step didn't already cover a neighbour. Repeated labels (several runs
          // sharing a date) are dropped for the same reason.
          const stepped = i % labelStep === 0;
          // The last point is worth labelling, but only if it isn't sitting
          // right next to a stepped one: its distance to the previous stepped
          // index is (N-1) mod step, so require at least 2 slots of clearance.
          const lastGap = (points.length - 1) % labelStep;
          const showLast = i === points.length - 1 && lastGap >= 2;
          if (!stepped && !showLast) return null;
          if (i > 0 && points[i - 1]!.label === p.label) return null;
          return (
            <text
              key={`l${i}`}
              x={cx(i)}
              y={height - 6}
              textAnchor="middle"
              className="fill-current text-[10px] opacity-50"
            >
              {p.label}
            </text>
          );
        })}
      </svg>

      {(primaryLabel || secondaryLabel) && hasSecondary && (
        <figcaption className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-sm" style={{ background: color }} aria-hidden />
            {primaryLabel}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-sm bg-foreground/20" aria-hidden />
            {secondaryLabel}
          </span>
        </figcaption>
      )}
    </figure>
  );
}
